import Dialog from '@mui/material/Dialog';
import React from 'react';
import { superAdminTooltips } from '../../../../shared/config/actionHelpCatalog';
import HelpTooltipTrigger from '../../../../shared/ui/common/HelpTooltipTrigger';
import {
  canCompleteDocumentSigner,
  canRunDocumentSignerDelivery,
  canUploadDocumentEvidence,
  getDocumentCommandPresentation,
} from '../../lib/legalDocumentPresentation';
import type { AdminApiError } from '../../types';
import type {
  AdminDocumentPackageDetail,
  AdminLegalSignatureEvidence,
  SignedArtifactUploadInput,
  LegalSignerCommand,
} from '../../legalTypes';
import { AdminButton, StatusPill } from '../components';

interface ExecutionDialogProps {
  open: boolean;
  companyName: string;
  packageVersion: number;
  busy: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const ExecutionDialog: React.FC<ExecutionDialogProps> = ({
  open,
  companyName,
  packageVersion,
  busy,
  onConfirm,
  onClose,
}) => {
  const required = `${companyName} / v${packageVersion}`;
  const [confirmation, setConfirmation] = React.useState('');
  React.useEffect(() => setConfirmation(''), [open]);
  return (
    <Dialog open={open} aria-labelledby="execution-confirmation-title" className="pipeex-admin-portal admin-legal-dialog" onClose={() => !busy && onClose()}>
      <form className="admin-legal-dialog__body admin-legal-dialog__body--confirm" onSubmit={(event) => { event.preventDefault(); void onConfirm(); }}>
        <header><span className="admin-legal-eyebrow">Immutable signature evidence</span><h2 id="execution-confirmation-title">Confirm record execution</h2></header>
        <p>Permanently mark this signatory complete from the verified returned evidence. This cannot be undone.</p>
        <label>Type execution confirmation<input autoFocus value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={required} /></label>
        <footer className="admin-legal-dialog__actions"><AdminButton variant="ghost" onClick={onClose}>Cancel</AdminButton><AdminButton type="submit" variant="danger" disabled={busy || confirmation !== required}>Record execution permanently</AdminButton></footer>
      </form>
    </Dialog>
  );
};

interface ResendDialogProps {
  signerName: string | null;
  companyName: string;
  packageVersion: number;
  busy: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const ResendDialog: React.FC<ResendDialogProps> = ({
  signerName,
  companyName,
  packageVersion,
  busy,
  onConfirm,
  onClose,
}) => (
  <Dialog open={Boolean(signerName)} aria-labelledby="resend-confirmation-title" className="pipeex-admin-portal admin-legal-dialog" onClose={() => !busy && onClose()}>
    <form className="admin-legal-dialog__body admin-legal-dialog__body--confirm" onSubmit={(event) => { event.preventDefault(); void onConfirm(); }}>
      <header><span className="admin-legal-eyebrow">New recipient delivery attempt</span><h2 id="resend-confirmation-title">Confirm package resend</h2></header>
      <p>{getDocumentCommandPresentation('resend').effect}</p>
      <dl className="admin-legal-detail-grid"><div><dt>Customer</dt><dd>{companyName}</dd></div><div><dt>Package</dt><dd>v{packageVersion}</dd></div><div><dt>Recipient</dt><dd>{signerName}</dd></div></dl>
      <footer className="admin-legal-dialog__actions"><AdminButton variant="ghost" disabled={busy} onClick={onClose}>Cancel resend</AdminButton><AdminButton type="submit" variant="primary" disabled={busy}>Resend package</AdminButton></footer>
    </form>
  </Dialog>
);

interface RevocationDialogProps {
  signerName: string | null;
  companyName: string;
  packageVersion: number;
  busy: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const RevocationDialog: React.FC<RevocationDialogProps> = ({
  signerName,
  companyName,
  packageVersion,
  busy,
  onConfirm,
  onClose,
}) => {
  const required = signerName
    ? `REVOKE ${companyName} / v${packageVersion} / ${signerName}`
    : '';
  const [confirmation, setConfirmation] = React.useState('');
  React.useEffect(() => setConfirmation(''), [signerName]);
  return (
    <Dialog open={Boolean(signerName)} aria-labelledby="revocation-confirmation-title" className="pipeex-admin-portal admin-legal-dialog" onClose={() => !busy && onClose()}>
      <form className="admin-legal-dialog__body admin-legal-dialog__body--confirm" onSubmit={(event) => { event.preventDefault(); void onConfirm(); }}>
      <header><span className="admin-legal-eyebrow">Recipient access revocation</span><h2 id="revocation-confirmation-title">Confirm delivery revocation</h2></header>
        <p>Revoke the outstanding delivery token for {signerName}. Prior delivery and evidence history remain immutable.</p>
        <label>Type revocation confirmation<input autoFocus value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={required} /></label>
        <footer className="admin-legal-dialog__actions"><AdminButton variant="ghost" disabled={busy} onClick={onClose}>Cancel revocation</AdminButton><AdminButton type="submit" variant="danger" disabled={busy || confirmation !== required}>Revoke recipient delivery</AdminButton></footer>
      </form>
    </Dialog>
  );
};

interface SignerListProps {
  detail: AdminDocumentPackageDetail;
  busy: boolean;
  onResend: (signerId: string) => void;
  onRevoke: (signerId: string) => void;
}

const SignerList: React.FC<SignerListProps> = ({ detail, busy, onResend, onRevoke }) => {
  if (detail.signers.length === 0) {
    return <p>No immutable recipient plan has been configured.</p>;
  }
  return (
    <>
      <ol className="admin-legal-signer-list">
        {detail.signers.map((signer) => {
          const available = canRunDocumentSignerDelivery(detail, signer.id);
          const successful = signer.state === 'signed';
          return (
            <li key={signer.id}>
              <div><strong data-visual-mask>{signer.personName}</strong><span>{signer.organizationName} · {signer.title}</span><span data-visual-mask>{signer.email}</span></div>
              <StatusPill tone={successful ? 'success' : signer.state === 'declined' ? 'critical' : 'info'}>[{successful ? '✓' : '•'}] {signer.state.replace(/_/g, ' ')}</StatusPill>
              <div className="admin-legal-inline-actions">
                <AdminButton disabled={busy || !available} onClick={() => onResend(signer.id)}>Resend package</AdminButton>
                <AdminButton variant="danger" disabled={busy || !available} onClick={() => onRevoke(signer.id)}>Revoke delivery</AdminButton>
              </div>
            </li>
          );
        })}
      </ol>
      {!detail.signers.some((signer) => canRunDocumentSignerDelivery(detail, signer.id))
        && <p className="admin-legal-disabled-reason">Delivery actions are available only for sent recipients while package execution remains open.</p>}
    </>
  );
};

interface StoredEvidenceListProps {
  detail: AdminDocumentPackageDetail;
  evidence: AdminLegalSignatureEvidence[] | null;
}

const evidenceTone = (verificationStatus: string) => {
  const normalized = verificationStatus.toLocaleLowerCase();
  if (normalized === 'verified') {
    return 'success' as const;
  }
  return normalized === 'quarantined' ? 'critical' as const : 'warning' as const;
};

const StoredEvidenceList: React.FC<StoredEvidenceListProps> = ({ detail, evidence }) => {
  if (evidence === null) {
    return (
      <p role="status" className="admin-legal-disabled-reason">
        Evidence history is unknown. Execution remains disabled until stored signature evidence is refreshed.
      </p>
    );
  }
  if (evidence.length === 0 || detail.signers.length === 0) {
    return null;
  }
  return (
    <>
      <h4>Stored returned evidence</h4>
      <ol className="admin-legal-signer-list" aria-label="Stored signature evidence">
        {detail.signers.flatMap((signer) =>
          evidence
            .filter((record) => record.signerId === signer.id)
            .map((record) => (
              <li key={record.id}>
                <div>
                  <strong data-visual-mask>{signer.personName}</strong>
                  <span>Returned evidence recorded.</span>
                  <span>Size: {record.artifactSizeBytes.toLocaleString()} bytes · {record.signatureMethod}</span>
                  <span data-visual-mask>Received {record.receivedAtUtc}</span>
                </div>
                <StatusPill tone={evidenceTone(record.verificationStatus)}>
                  [{record.verificationStatus.toLocaleLowerCase() === 'verified' ? '✓' : '!'}] {record.verificationStatus}
                </StatusPill>
              </li>
            )))}
      </ol>
    </>
  );
};



interface SignatureEvidencePanelProps {
  detail: AdminDocumentPackageDetail;
  companyName: string;
  busy: boolean;
  error: AdminApiError | null;
  evidence: AdminLegalSignatureEvidence[] | null;
  onUpload: (signerId: string, input: SignedArtifactUploadInput) => Promise<AdminLegalSignatureEvidence>;
  onComplete: (signerId: string) => Promise<AdminDocumentPackageDetail>;
  onSignerCommand: (
    signerId: string,
    command: LegalSignerCommand
  ) => Promise<AdminDocumentPackageDetail>;
}

const useDeliveryDialogs = (
  detail: AdminDocumentPackageDetail,
  onSignerCommand: SignatureEvidencePanelProps['onSignerCommand']
) => {
  const [resendSignerId, setResendSignerId] = React.useState<string | null>(null);
  const [revocationSignerId, setRevocationSignerId] = React.useState<string | null>(null);
  const resendSigner = detail.signers.find((candidate) => candidate.id === resendSignerId) ?? null;
  const revocationSigner = detail.signers.find((candidate) => candidate.id === revocationSignerId) ?? null;
  const execute = async (
    signerId: string | null,
    command: LegalSignerCommand,
    onSuccess: () => void
  ) => {
    if (!signerId) {
      return;
    }
    try {
      await onSignerCommand(signerId, command);
      onSuccess();
    } catch {
      return;
    }
  };
  return {
    resendSigner,
    revocationSigner,
    requestResend: setResendSignerId,
    requestRevoke: setRevocationSignerId,
    closeResend: () => setResendSignerId(null),
    closeRevoke: () => setRevocationSignerId(null),
    confirmResend: () => execute(resendSignerId, 'resend', () => setResendSignerId(null)),
    confirmRevoke: () => execute(revocationSignerId, 'revoke', () => setRevocationSignerId(null)),
  };
};

const SignatureEvidencePanel: React.FC<SignatureEvidencePanelProps> = ({
  detail,
  companyName,
  busy,
  error,
  evidence,
  onUpload,
  onComplete,
  onSignerCommand,
}) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [signatory, setSignatory] = React.useState('');
  const [authority, setAuthority] = React.useState(false);
  const [executionOpen, setExecutionOpen] = React.useState(false);
  const [selectedSignerId, setSelectedSignerId] = React.useState(detail.signers[0]?.id ?? '');
  const delivery = useDeliveryDialogs(detail, onSignerCommand);
  React.useEffect(() => {
    if (!detail.signers.some((signer) => signer.id === selectedSignerId)) {
      setSelectedSignerId(detail.signers[0]?.id ?? '');
    }
  }, [detail.signers, selectedSignerId]);
  const signer = detail.signers.find((candidate) => candidate.id === selectedSignerId) ?? null;
  const uploadAvailable = signer ? canUploadDocumentEvidence(detail, signer.id) : false;
  const completionAvailable = signer ? canCompleteDocumentSigner(detail, signer.id, evidence) : false;
  const upload = async () => {
    if (!file || !signer || !uploadAvailable) {
      return;
    }
    try {
      await onUpload(signer.id, {
        file,
        declaredSignatoryName: signatory,
        authorityConfirmed: authority,
        signatureMethod: 'ManualWetInk',
      });
      setFile(null);
      setSignatory('');
      setAuthority(false);
    } catch {
      return;
    }
  };
  const complete = async () => {
    if (!signer || !completionAvailable) {
      return;
    }
    try {
      await onComplete(signer.id);
      setExecutionOpen(false);
    } catch {
      return;
    }
  };

  return (
    <section className="admin-legal-card" aria-labelledby="signature-evidence-title">
      <div className="admin-legal-card__heading"><div><span className="admin-legal-eyebrow">Delivery and returned evidence</span><h3 id="signature-evidence-title">Signatories</h3></div><HelpTooltipTrigger label="Returned signature evidence" help={superAdminTooltips.returnedSignatureEvidence} placement="bottom-start" /></div>
      <SignerList detail={detail} busy={busy} onResend={delivery.requestResend} onRevoke={delivery.requestRevoke} />
      <div className="admin-legal-evidence-form">
        <label>Evidence recipient<select value={selectedSignerId} disabled={busy || detail.signers.length === 0} onChange={(event) => setSelectedSignerId(event.target.value)}><option value="">Select a recipient</option>{detail.signers.map((item) => <option key={item.id} value={item.id}>{item.personName} · {item.organizationName}</option>)}</select></label>
        <label>Returned signed file<input type="file" accept=".pdf,.doc,.docx,application/pdf" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>
        <label>Declared signatory<input value={signatory} onChange={(event) => setSignatory(event.target.value)} /></label>
        <label className="admin-legal-check"><input type="checkbox" checked={authority} onChange={(event) => setAuthority(event.target.checked)} />Authority confirmed</label>
        <div className="admin-legal-inline-actions">
          <AdminButton variant="primary" disabled={busy || !uploadAvailable || !file || !signatory.trim() || !authority} onClick={() => void upload()}>Upload returned signature</AdminButton>
          <AdminButton variant="danger" disabled={busy || !completionAvailable} onClick={() => setExecutionOpen(true)}>Record execution</AdminButton>
        </div>
        {signer && !uploadAvailable && <p className="admin-legal-disabled-reason">Returned evidence can be recorded only for a sent recipient while execution remains open.</p>}
        {signer && uploadAvailable && !completionAvailable && <p className="admin-legal-disabled-reason">Record verified returned evidence for this recipient before completing execution.</p>}
      </div>
      {error?.code === 'artifact_quarantined' && <div role="alert" className="admin-legal-callout admin-legal-callout--warning">{error.message}</div>}
      <StoredEvidenceList detail={detail} evidence={evidence} />
      <ExecutionDialog open={executionOpen} companyName={companyName} packageVersion={detail.sequence} busy={busy} onConfirm={complete} onClose={() => setExecutionOpen(false)} />
      <ResendDialog signerName={delivery.resendSigner?.personName ?? null} companyName={companyName} packageVersion={detail.sequence} busy={busy} onConfirm={delivery.confirmResend} onClose={delivery.closeResend} />
      <RevocationDialog signerName={delivery.revocationSigner?.personName ?? null} companyName={companyName} packageVersion={detail.sequence} busy={busy} onConfirm={delivery.confirmRevoke} onClose={delivery.closeRevoke} />
    </section>
  );
};

export default SignatureEvidencePanel;
